import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, race, throwError } from 'rxjs';
import { catchError, map, timeout, delay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class IpAddressService {

  constructor(private http: HttpClient) { }

  // Main method with multiple fallback options and guaranteed timeout
  getIpAddress(): Observable<string> {
    // Use WebRTC primarily with guaranteed timeout fallback
    return race(
      this.getLocalIpAddressAsObservable(),
      of('Not available').pipe(delay(2500)) // Hard timeout fallback after 2.5 seconds
    ).pipe(
      timeout(3000), // Absolute max timeout
      catchError(() => of('Not available')) // Guaranteed fallback
    );
  }

  getLocalIpAddressAsObservable(): Observable<string> {
    return new Observable<string>(observer => {
      const timeoutId = setTimeout(() => {
        observer.next('Unable to determine');
        observer.complete();
      }, 2000);

      this.getLocalIpAddress()
        .then(ip => {
          clearTimeout(timeoutId);
          observer.next(ip);
          observer.complete();
        })
        .catch(() => {
          clearTimeout(timeoutId);
          observer.next('Unable to determine');
          observer.complete();
        });
    });
  }

  getLocalIpAddress(): Promise<string> {
    return new Promise((resolve, reject) => {
      let resolved = false;
      
      // Guaranteed timeout
      const hardTimeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('WebRTC timeout'));
        }
      }, 1500);

      try {
        const rtc = new RTCPeerConnection({ 
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] 
        });
        
        rtc.createDataChannel('');
        rtc.onicecandidate = (event) => {
          if (resolved) return;
          
          if (event.candidate) {
            const candidate = event.candidate.candidate;
            const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
            if (ipMatch && !ipMatch[1].startsWith('127.') && !ipMatch[1].startsWith('169.254.')) {
              resolved = true;
              clearTimeout(hardTimeout);
              rtc.close();
              resolve(ipMatch[1]);
              return;
            }
          }
        };
        
        rtc.createOffer()
          .then(offer => rtc.setLocalDescription(offer))
          .catch(() => {
            if (!resolved) {
              resolved = true;
              clearTimeout(hardTimeout);
              rtc.close();
              reject(new Error('WebRTC offer failed'));
            }
          });
        
      } catch (error) {
        if (!resolved) {
          resolved = true;
          clearTimeout(hardTimeout);
          console.warn('WebRTC not supported or failed:', error);
          reject(error);
        }
      }
    });
  }

  getIpAddressQuick(): Observable<string> {
    if (navigator.geolocation) {
      return new Observable<string>(observer => {
        observer.next('Browser-based');
        observer.complete();
      });
    } else {
      return of('Not available');
    }
  }
}